import * as signalR from '@microsoft/signalr'
import { appConfig } from './app-config'
import { authStore } from '../store/modules/auth.store'
import { notificationsStore } from '../store/modules/notifications.store'
import { Notification, NotificationHubMessage } from '../models/notification'
import emitter from './emitter'

const MESSAGE_RECEIVED = 'message-received'
const TOAST_DURATION_MS = 10000

function fromHub({ message_id, ...message }: NotificationHubMessage): Notification {
    return { ...message, id: message_id }
}

class MessageCenterHub {
    private connection: signalR.HubConnection | null = null

    async start(): Promise<void> {
        await this.stop()

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(appConfig.apiUrl + '/messagecenterhub', {
                accessTokenFactory: () => authStore.encodedToken ?? '',
            })
            .withAutomaticReconnect()
            .build()

        connection.on(MESSAGE_RECEIVED, (message: NotificationHubMessage) => {
            const notification = fromHub(message)
            const isNew = !notificationsStore.notifications.some(x => x.id === notification.id)
            notificationsStore.receive(notification)
            if (isNew && notification.presentation === 'toast') {
                emitter.emit('toast', { message: notification.subject, duration: TOAST_DURATION_MS })
            }
        })
        connection.onreconnected(() => this.loadMessages())
        this.connection = connection

        try {
            await connection.start()
        } catch (error) {
            console.error('Message center hub connection failed', error)
        }

        if (this.connection === connection) {
            await this.loadMessages()
        }
    }

    async stop(): Promise<void> {
        const connection = this.connection
        this.connection = null
        notificationsStore.clear()
        if (connection) {
            connection.off(MESSAGE_RECEIVED)
            await connection.stop()
        }
    }

    private async loadMessages() {
        try {
            await notificationsStore.fetch()
        } catch (error) {
            console.error('Loading messages failed', error)
        }
    }
}

export const messageCenterHub = new MessageCenterHub()
